// Feb-2026 · Auditoría PLD (panel FELVORA) — Super_admin actuando como auditor externo.
//
// Diseño:
// - Selector de entidad que SOLO lista las que autorizaron. Sin autorización no aparecen.
// - Al elegir una, monta PaqueteAuditoria en modo felvora (props: entityId + ejercicio).
// - Panel intencionalmente sobrio: enfoca al auditor en su trabajo.
import { useCallback, useEffect, useState } from "react";
import { Building2, ShieldCheck, ClipboardCheck, Loader2, Info } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PaqueteAuditoria from "@/components/pld/PaqueteAuditoria";

export default function AuditoriaFelvoraAdmin() {
  const [loading, setLoading] = useState(true);
  const [entidades, setEntidades] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auditoria-felvora/entidades-autorizadas");
      const list = data?.entidades || [];
      setEntidades(list);
      // Autoselect si solo hay una
      if (list.length === 1) setSelectedId(list[0].id);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const seleccionada = entidades.find((e) => e.id === selectedId);
  const auth = seleccionada?.autorizacion_auditoria_felvora || {};

  return (
    <div data-testid="pagina-auditoria-felvora-admin">
      <PageHeader
        title="Auditoría PLD (panel FELVORA)"
        subtitle="Genera el paquete de evidencia CNBV y el Informe .docx de las entidades que autorizaron a FELVORA como auditor externo."
      />

      <div className="space-y-5 p-6 md:p-8">

        {/* Aviso normativo */}
        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-600">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>
            <strong className="text-slate-800">FELVORA como auditor externo (Disposición 52ª).</strong>{" "}
            Solo aparecen entidades con <strong>autorización vigente</strong>. La entidad puede revocarla en su
            Configuración; cuando lo haga, dejará de listarse aquí (los documentos ya emitidos se conservan
             accesibles para su descarga por retención regulatoria de 5 años).
          </p>
        </div>

        {/* Selector de entidad */}
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="selector-entidad-felvora">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full sm:w-96">
              <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Building2 className="h-3.5 w-3.5" /> Entidad autorizada
              </label>
              {loading ? (
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando entidades autorizadas…
                </div>
              ) : entidades.length === 0 ? (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="sin-entidades-autorizadas">
                  <Info className="h-4 w-4 shrink-0" />
                  <span>No hay entidades con autorización vigente. Cada entidad debe otorgar la autorización desde su Configuración → Auditoría externa PLD/FT.</span>
                </div>
              ) : (
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger data-testid="select-entidad-felvora"><SelectValue placeholder="Selecciona una entidad" /></SelectTrigger>
                  <SelectContent>
                    {entidades.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`entidad-opt-${e.id}`}>
                        {e.razon_social || e.nombre_comercial || e.id}
                        <span className="ml-2 text-[10.5px] text-slate-400">
                          · ejercicio {e.autorizacion_auditoria_felvora?.ejercicio}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {seleccionada && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="detalle-autorizacion">
                <p className="flex items-center gap-1.5">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  <span>
                    Autorización vigente · <strong>ejercicio {auth.ejercicio}</strong> ·
                    otorgada por <strong>{auth.otorgada_por_nombre}</strong> el <strong>{fdate(auth.otorgada_at)}</strong>
                  </span>
                </p>
                <p className="mt-0.5 text-[10.5px] text-emerald-700">
                  Cada acción quedará registrada en la bitácora visible por la entidad.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contenido del auditor — solo cuando hay entidad seleccionada */}
        {seleccionada ? (
          <div data-testid="contenido-auditor">
            <div className="mb-3 flex items-center gap-2">
              <Badge className="bg-slate-900 text-white">{seleccionada.razon_social || seleccionada.id}</Badge>
              <Badge variant="outline" className="font-mono text-[10.5px]">Ejercicio {auth.ejercicio}</Badge>
              {seleccionada.rfc && (
                <Badge variant="outline" className="text-[10.5px] text-slate-500">RFC {seleccionada.rfc}</Badge>
              )}
            </div>
            <PaqueteAuditoria
              felvoraCtx={{
                entityId: seleccionada.id,
                ejercicio: Number(auth.ejercicio) || new Date().getFullYear(),
                razonSocial: seleccionada.razon_social,
              }}
            />
          </div>
        ) : (
          !loading && entidades.length > 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center text-sm text-slate-500" data-testid="sin-entidad-seleccionada">
              Selecciona una entidad para operar la auditoría.
            </div>
          )
        )}

      </div>
    </div>
  );
}
